import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  createWorkDetail,
  getWorkDetails,
  updateWorkDetail,
  deleteWorkDetail,
} from "../../api/const";
import { getActivitiesByDate } from "../../api/process";

export default function WorkDetailPage() {
  const today = new Date().toLocaleDateString("sv-SE");
  const { id: projectId } = useParams();

  const [date, setDate] = useState(today);
  const [rows, setRows] = useState([{ activity: "", content: "" }]);
  const [saving, setSaving] = useState(false);
  const [activities, setActivities] = useState([]);
  const [existing, setExisting] = useState([]);

  // 활동 불러오기
  useEffect(() => {
    (async () => {
      try {
        const data = await getActivitiesByDate(date);
        setActivities(data.results || data);
      } catch (err) {
        console.error("활동 불러오기 실패", err);
      }
    })();
  }, [date]);

  // 저장된 상세작업 불러오기
  useEffect(() => {
    if (!projectId || !date) return;
    (async () => {
      try {
        const data = await getWorkDetails(date);
        setExisting(data.results);
      } catch (err) {
        console.error("상세작업 불러오기 실패", err);
      }
    })();
  }, [date, projectId]);

  const addRow = () => setRows([...rows, { activity: "", content: "" }]);

  const resetRows = () => setRows([{ activity: "", content: "" }]);

  const deleteRows = () =>
    setRows((prev) => {
      if (prev.length === 1) return [{ activity: "", content: "" }];
      const next = [...prev];
      next.pop();
      return next;
    });

  const updateRow = (index, field, value) => {
    const next = [...rows];
    next[index][field] = value;
    setRows(next);
  };

  const save = async () => {
    if (!date) {
      alert("작업일을 선택하세요.");
      return;
    }
    setSaving(true);
    try {
      for (const row of rows) {
        if (!row.activity || !row.content) continue;
        await createWorkDetail({
          project: projectId,
          activity: row.activity,
          date,
          content: row.content,
        });
      }
      alert("저장 완료!");
      setRows([{ activity: "", content: "" }]);
      const data = await getWorkDetails(date);
      setExisting(data.results);
    } catch (err) {
      console.error(err);
      alert("저장 중 오류 발생");
    } finally {
      setSaving(false);
    }
  };

  const fieldClass =
    "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition";
  const textAreaClass =
    "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm min-h-[96px] shadow-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 transition";

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-md mx-auto px-4 py-6 pb-28 space-y-6">
        <header className="space-y-1">
          <p className="text-xs text-gray-500">시공관리 · 공사일보</p>
          <h1 className="text-title">상세작업 입력</h1>
          <p className="text-muted">
            Activity 기준으로 세분화된 작업 내역을 기록하세요.
          </p>
        </header>

        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-semibold text-slate-800">
              작업일
            </label>
            <span className="text-xs text-gray-500">YYYY-MM-DD</span>
          </div>
          <input
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              setRows([{ activity: "", content: "" }]);
            }}
            className={fieldClass}
          />
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-subtitle">저장된 작업</h3>
              <p className="text-muted">오늘 입력된 상세 작업 목록입니다.</p>
            </div>
            {existing.length > 0 && (
              <span className="px-2.5 py-1 text-xs rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100">
                {existing.length}건
              </span>
            )}
          </div>

          {existing.length ? (
            <ul className="space-y-3">
              {existing.map((w) => (
                <li
                  key={w.id}
                  className="relative rounded-xl border border-slate-200 bg-slate-50 p-3 shadow-sm"
                >
                  {!w._editing && (
                    <button
                      className="absolute top-2 right-2 text-slate-400 hover:text-red-500 transition"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (window.confirm("삭제하시겠습니까?")) {
                          await deleteWorkDetail(w.id);
                          const data = await getWorkDetails(date);
                          setExisting(data.results);
                        }
                      }}
                    >
                      ✕
                    </button>
                  )}

                  {w._editing ? (
                    <div className="space-y-3">
                      <select
                        value={w.activity}
                        onChange={(e) =>
                          setExisting((prev) =>
                            prev.map((x) =>
                              x.id === w.id
                                ? { ...x, activity: e.target.value }
                                : x
                            )
                          )
                        }
                        className={fieldClass}
                      >
                        {activities.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>
                      <textarea
                        value={w.content}
                        onChange={(e) =>
                          setExisting((prev) =>
                            prev.map((x) =>
                              x.id === w.id
                                ? { ...x, content: e.target.value }
                                : x
                            )
                          )
                        }
                        className={textAreaClass}
                      />
                      <div className="flex gap-2 justify-end">
                        <button
                          className="btn-save-sm"
                          onClick={async (e) => {
                            e.stopPropagation();
                            await updateWorkDetail(w.id, {
                              project: projectId,
                              activity: w.activity,
                              date,
                              content: w.content,
                            });
                            const data = await getWorkDetails(date);
                            setExisting(data.results);
                          }}
                        >
                          저장
                        </button>
                        <button
                          className="btn-cancel-sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExisting((prev) =>
                              prev.map((x) =>
                                x.id === w.id ? { ...x, _editing: false } : x
                              )
                            );
                          }}
                        >
                          취소
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="space-y-2 cursor-pointer"
                      onClick={() =>
                        setExisting((prev) =>
                          prev.map((x) =>
                            x.id === w.id ? { ...x, _editing: true } : x
                          )
                        )
                      }
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-slate-900">
                          {w.activity_name}
                        </span>
                        <span className="text-xs text-gray-500">탭하여 편집</span>
                      </div>
                      <div className="text-sm text-slate-600 whitespace-pre-line">
                        {w.content}
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-center">
              <p className="text-muted">저장된 작업이 없습니다.</p>
            </div>
          )}
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 space-y-3">
          <div className="space-y-1">
            <h3 className="text-subtitle">상세작업 입력</h3>
            <p className="text-muted">Activity와 내용을 입력 후 추가하세요.</p>
          </div>

          <div className="space-y-3">
            {rows.map((row, idx) => (
              <div
                key={idx}
                className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2 shadow-sm"
              >
                <select
                  value={row.activity}
                  onChange={(e) => updateRow(idx, "activity", e.target.value)}
                  className={fieldClass}
                >
                  <option value="">Activity 선택</option>
                  {activities.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>

                <textarea
                  value={row.content}
                  onChange={(e) => updateRow(idx, "content", e.target.value)}
                  className={textAreaClass}
                  placeholder="작업 상세 내용을 입력하세요"
                />
              </div>
            ))}
          </div>

        </section>
      </div>
      {/* 모바일 하단 고정 액션바 */}
      <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
        <div className="max-w-md mx-auto grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={addRow}
            className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.97] transition-all"
          >
            + 추가
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="w-full h-11 rounded-full bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 active:scale-[0.97] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "저장 중..." : "저장"}
          </button>
          <button
            type="button"
            onClick={deleteRows}
            className="w-full h-11 rounded-full border border-red-200 bg-red-50 text-red-600 text-sm font-medium hover:bg-red-100 active:scale-[0.97] transition-all"
          >
            삭제
          </button>
        </div>
      </footer>
    </div>
  );
}
